import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as yup from 'yup';
import { userAction, profileAction } from "@redux/modules";

// Toastify
import showMessage from "@components/ShowMessage";
import showErrMessage from "@components/ShowErrMessage";

const Address = () => {
  const dispatch = useDispatch();
  const address = useSelector((store) => store.profile.address);

  const [validated, setValidated] = useState(false);
  const countries = useSelector((store) => store.profile.countryList);
  const provinces = useSelector((store) => store.profile.provinceList);
  const counties = useSelector((store) => store.profile.countyList);
  const districts = useSelector((store) => store.profile.districtList);

  // Form Datas
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [county, setCounty] = useState("");
  const [district, setDistrict] = useState("");
  const [province, setProvince] = useState("");
  const [street, setStreet] = useState("");
  const [apartmentNo, setApartmentNo] = useState("");
  const [flatNo, setFlatNo] = useState("");
  const [editAddress, setEditAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addNewAddressButtonActive, setAddNewAddressButtonActive] =
    useState(true);

  const addressSchema = yup.object({
    name: yup
      .string()
      .required("Adres adı zorunludur!")
      .min(3, "Adres adı en az 3 karakter olmalıdır!")
      .max(20, "Adres adı en fazla 20 karakter olmalıdır!"),
    country: yup.string().required("Ülke seçimi zorunludur!"),
    province: yup.string().required("Şehir seçimi zorunludur!"),
    county: yup.string().required("İlçe seçimi zorunludur!"),
    district: yup.string().required("Mahalle seçimi zorunludur!"),
    street: yup
      .string()
      .required("Cadde/Sokak zorunludur!")
      .min(3, "Cadde/Sokak en az 3 karakter olmalıdır!")
      .max(100, "Cadde/Sokak en fazla 100 karakter olmalıdır!"),
    apartmentNo: yup
      .string()
      .required("Apartman No/Adı zorunludur!")
      .max(100, "Apartman No/Adı en fazla 100 karakter olmalıdır!"),
    flatNo: yup
      .number()
      .required("Daire No zorunludur!")
      .min(1, "Daire No en az 1 olmalıdır!")
      .max(999, "Daire No en fazla 999 olmalıdır!"),
  });

  // NOT: Bağımlı dropdown'ları lokal state ile değil Formik'in setFieldValue'ı
  // ile sıfırlıyoruz. Aksi halde lokal state değişimi enableReinitialize'ı
  // tetikleyip formu yeniden başlatıyor ve girilmiş alanları (örn. adres adı)
  // siliyordu.
  const selectCountry = (event, setFieldValue) => {
    const value = event.target.value;
    setFieldValue("province", ""); // İl seçimini sıfırla
    setFieldValue("county", ""); // İlçe seçimini sıfırla
    setFieldValue("district", ""); // Mahalle seçimini sıfırla
    dispatch(profileAction.resetAddressCounty());
    dispatch(profileAction.resetAddressDistrict());
    dispatch(profileAction.getAddressProvince(value));
  };

  const selectProvince = (event, setFieldValue) => {
    const value = event.target.value;
    setFieldValue("county", ""); // İlçe seçimini sıfırla
    setFieldValue("district", ""); // Mahalle seçimini sıfırla
    dispatch(profileAction.resetAddressDistrict());
    dispatch(profileAction.getAddressCounty(value));
  };

  const selectCounty = (event, setFieldValue) => {
    const value = event.target.value;
    setFieldValue("district", ""); // Mahalle seçimini sıfırla
    dispatch(profileAction.getAddressDistrict(value));
  };

  const resetForm = () => {
    setName("");
    setCountry("");
    setCounty("");
    setDistrict("");
    setProvince("");
    setStreet("");
    setApartmentNo("");
    setFlatNo("");
    setEditAddress(false);
    setSelectedAddressId(null);
    setAddNewAddressButtonActive(true);
  };

  const handleSaveAddress = async (values, { resetForm }) => {
    try {
      await dispatch(
        profileAction.addAddress({
          name: values.name,
          country: values.country,
          province: values.province,
          county: values.county,
          district: values.district,
          street: values.street,
          apartmentNo: values.apartmentNo,
          flatNo: values.flatNo,
        })
      );
      showMessage("Yeni Adres Eklendi!");
      resetForm();
      dispatch(profileAction.getAddress());
      setAddNewAddressButtonActive(true);
    } catch (err) {
      showErrMessage(err.message);
    }
  };

  const handleEditAddress = async (values, { resetForm }) => {
    try {
      await dispatch(
        profileAction.addAddress({
          name: values.name,
          country: values.country,
          province: values.province,
          county: values.county,
          district: values.district,
          street: values.street,
          apartmentNo: values.apartmentNo,
          flatNo: values.flatNo,
          addressId: selectedAddressId,
        })
      );
      showMessage("Adres Güncellendi!");
      resetForm();
      dispatch(profileAction.getAddress());
      setAddNewAddressButtonActive(true);
    } catch (err) {
      showErrMessage(err.message);
    }
  };

  const handleDeleteAddress = (e, addressId) => {
    e.preventDefault();
    e.stopPropagation();

    dispatch(profileAction.deleteAddress(addressId))
      .then((e) => {
        if (e.isHttpSuccess) {
          showMessage("Adres Silindi!");
          dispatch(profileAction.getAddress());
        }
      })
      .catch((err) => {
        showErrMessage(err.message);
      });
  };

  const changeAddressStatus = (e, addressId) => {
    e.preventDefault();

    dispatch(profileAction.changeAddressStatus(addressId))
      .then((e) => {
        if (e.isHttpSuccess) {
          showMessage("Sipariş Adresi Güncellendi!");
          dispatch(profileAction.getAddress());
        }
      })
      .catch((err) => {
        showErrMessage(err.message);
      });
  };

  const handleEditClick = (event, addressId) => {
    event.preventDefault();
    setEditAddress(true);
    setAddNewAddressButtonActive(false);
    setSelectedAddressId(addressId);

    const selectedAddress = address.find(
      (addr) => addr.addressId === addressId
    );

    if (selectedAddress) {
      setName(selectedAddress.name);
      setCountry(selectedAddress.countryId);
      dispatch(profileAction.getAddressProvince(selectedAddress.countryId));
      setProvince(selectedAddress.provinceId);
      dispatch(profileAction.getAddressCounty(selectedAddress.provinceId));
      setCounty(selectedAddress.countyId);
      dispatch(profileAction.getAddressDistrict(selectedAddress.countyId));
      setDistrict(selectedAddress.districtId);
      setStreet(selectedAddress.street);
      setApartmentNo(selectedAddress.apartmentNo);
      setFlatNo(selectedAddress.flatNo);
    }
  };

  useEffect(() => {
    // Get All Address
    dispatch(profileAction.getAddress());
    dispatch(profileAction.getAddressCountry());

    return () => {};
  }, [dispatch]);

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-sm-6 mb-4">
        <h2 className="mb-0">Adres Bilgilerim</h2>
        {addNewAddressButtonActive ? (
          <button
            type="button"
            className="btn btn-link text-decoration-none"
            onClick={(event) => {
              setAddNewAddressButtonActive(false);
              setEditAddress(false);
            }}
          >
            <i className="fas fa-plus-circle"></i> Yeni Adres Ekle
          </button>
        ) : null}
      </div>
      <p className="mb-sm-5 mb-4 fw-light">
        Adres alanına girdiğiniz bilgiler, hediye siparişlerinizin gönderimi
        için kullanılacağından bilgilerinizin doğruluğundan lütfen emin olunuz.
        Ev ya da işyeri adresi gibi birden fazla adres girişi yapabilirsiniz.
        Yalnızca siparişinizin gönderilmesini istediğiniz adresi "Sipariş
        Adresi" olarak seçmeniz gerekmektedir. Sipariş vermeden önce mutlaka
        doğru adresin sipariş adresi olarak seçildiğini kontrol etmelisiniz.
      </p>

      {addNewAddressButtonActive === false ? (
        <Formik
          validationSchema={addressSchema}
          onSubmit={editAddress ? handleEditAddress : handleSaveAddress}
          initialValues={{
            name: name,
            country: country,
            province: province,
            county: county,
            district: district,
            street: street,
            apartmentNo: apartmentNo,
            flatNo: flatNo,
          }}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <div className="d-flex align-items-center justify-content-between mb-sm-5 mb-4">
                <h5 className="text-blue mb-0">
                  {editAddress ? "Adresi Düzenle" : "Yeni Adres Ekle"}
                </h5>
                <button
                  type="button"
                  className="btn btn-link text-red text-decoration-none"
                  onClick={() => {
                    resetForm();
                  }}
                >
                  {addNewAddressButtonActive === false ? (
                    <small>
                      <i className="fas fa-times me-2"></i>Vazgeç
                    </small>
                  ) : null}
                </button>
              </div>
              <div className="row row-cols-md-3 row-cols-1 gy-4 pb-sm-5 pb-4 mb-sm-5 mb-4">
                <div className="col">
                  <div className="form-floating">
                    <input
                      type="text"
                      name="name"
                      className={`form-control ${
                        errors.name && touched.name ? "is-invalid" : ""
                      }`}
                      placeholder="Adres Adı"
                      maxLength="20"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <label>Adres Adı</label>
                    {errors.name && touched.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>
                </div>
                <div className="col">
                  <div className="form-floating">
                    <select
                      name="country"
                      className={`form-select ${
                        errors.country && touched.country ? "is-invalid" : ""
                      }`}
                      value={values.country}
                      onChange={(e) => {
                        handleChange(e);
                        selectCountry(e, setFieldValue);
                      }}
                      onBlur={handleBlur}
                    >
                      <option value="">Seçiniz</option>
                      {countries?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <label>Ülke</label>
                    {errors.country && touched.country && (
                      <div className="invalid-feedback">{errors.country}</div>
                    )}
                  </div>
                </div>
                <div className="col">
                  <div className="form-floating">
                    <select
                      name="province"
                      className={`form-select ${
                        errors.province && touched.province ? "is-invalid" : ""
                      }`}
                      value={values.province}
                      onChange={(e) => {
                        handleChange(e);
                        selectProvince(e, setFieldValue);
                      }}
                      onBlur={handleBlur}
                    >
                      <option value="">Seçiniz</option>
                      {provinces?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <label>Şehir</label>
                    {errors.province && touched.province && (
                      <div className="invalid-feedback">{errors.province}</div>
                    )}
                  </div>
                </div>
                <div className="col">
                  <div className="form-floating">
                    <select
                      name="county"
                      className={`form-select ${
                        errors.county && touched.county ? "is-invalid" : ""
                      }`}
                      value={values.county}
                      onChange={(e) => {
                        handleChange(e);
                        selectCounty(e, setFieldValue);
                      }}
                      onBlur={handleBlur}
                    >
                      <option value="">Seçiniz</option>
                      {counties?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <label>İlçe</label>
                    {errors.county && touched.county && (
                      <div className="invalid-feedback">{errors.county}</div>
                    )}
                  </div>
                </div>

                <div className="col">
                  <div className="form-floating">
                    <select
                      name="district"
                      className={`form-select ${
                        errors.district && touched.district ? "is-invalid" : ""
                      }`}
                      value={values.district}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      <option value="">Seçiniz</option>
                      {districts?.map((item, i) => (
                        <option value={item.id} key={i}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <label>Mahalle</label>
                    {errors.district && touched.district && (
                      <div className="invalid-feedback">{errors.district}</div>
                    )}
                  </div>
                </div>

                <div className="col">
                  <div className="form-floating">
                    <input
                      type="text"
                      name="street"
                      className={`form-control ${
                        errors.street && touched.street ? "is-invalid" : ""
                      }`}
                      placeholder="Cadde/Sokak"
                      maxLength="100"
                      value={values.street}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <label>Cadde/Sokak</label>
                    {errors.street && touched.street && (
                      <div className="invalid-feedback">{errors.street}</div>
                    )}
                  </div>
                </div>
                <div className="col">
                  <div className="form-floating">
                    <input
                      type="text"
                      name="apartmentNo"
                      className={`form-control ${
                        errors.apartmentNo && touched.apartmentNo
                          ? "is-invalid"
                          : ""
                      }`}
                      placeholder="Apartman No/Adı"
                      maxLength="100"
                      value={values.apartmentNo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <label>Apartman No/Adı</label>
                    {errors.apartmentNo && touched.apartmentNo && (
                      <div className="invalid-feedback">
                        {errors.apartmentNo}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col">
                  <div className="form-floating">
                    <input
                      type="number"
                      name="flatNo"
                      className={`form-control ${
                        errors.flatNo && touched.flatNo ? "is-invalid" : ""
                      }`}
                      placeholder="Apartman No/Adı"
                      maxLength="100"
                      value={values.flatNo}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <label>Daire No</label>
                    {errors.flatNo && touched.flatNo && (
                      <div className="invalid-feedback">{errors.flatNo}</div>
                    )}
                  </div>
                </div>

                <div className="col">
                  <input
                    type="hidden"
                    name="addressId"
                    defaultValue={
                      editAddress && selectedAddressId ? selectedAddressId : ""
                    }
                  />
                  <button
                    type="submit"
                    className="btn btn-blue py-sm-3 py-2 px-sm-4 px-3 w-100"
                  >
                    {editAddress ? "Düzenle" : "Ekle"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      ) : null}

      {address && address.length > 0 ? (
        <React.Fragment>
          <h5 className="text-blue mb-sm-5 mb-4">Adreslerim</h5>
          <div className="row row-cols-xl-3 row-cols-md-2 row-cols-1 gy-4">
            {address.map((item, i) => (
              <div className="col" key={item.addressId}>
                <div className="h-100 bg-white shadow-lg rounded-3 p-4 position-relative">
                  <h6
                    className="text-capitalize text-truncate"
                    style={{ maxWidth: "90%" }}
                  >
                    {item.name}
                  </h6>
                  <p className="mb-0">
                    {item.countyName} / {item.provinceName}
                  </p>
                  <div className="mt-4 d-flex align-items-center">
                    <button
                      style={{ padding: 0, outline: "none" }}
                      type="button"
                      className="btn btn-link text-decoration-none small"
                      onClick={(event) =>
                        handleEditClick(event, item.addressId)
                      }
                    >
                      <i className="fas fa-edit me-2"></i>Düzenle
                    </button>
                    {item?.orderAddress ? (
                      <span className="small text-green ms-auto">
                        <i className="fas fa-check-circle me-2"></i>Sipariş
                        Adresi
                      </span>
                    ) : (
                      <a
                        href="#"
                        className="text-decoration-none small ms-auto"
                        onClick={(event) =>
                          changeAddressStatus(event, item.addressId)
                        }
                      >
                        <i className="fas fa-circle fw-light me-2"></i>Sipariş
                        Adresi
                      </a>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none text-red position-absolute top-0 end-0 m-2"
                    onClick={(event) =>
                      handleDeleteAddress(event, item.addressId)
                    }
                  >
                    <i className="fas fa-trash-alt small"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default Address;
